const OPEN_TAG = '<think>';
const CLOSE_TAG = '</think>';

export interface ParsedAssistantContent {
  /** Concatenated text from every `<think>` segment, or `null` if there was none. */
  reasoning: string | null;
  /** Content with all `<think>...</think>` segments removed. */
  answer: string;
  /** True while a `<think>` segment is open and hasn't been closed yet (still streaming). */
  reasoningStreaming: boolean;
}

/**
 * Some models (e.g. reasoning-tuned Fireworks models) emit their chain-of-thought inline in the
 * chat-completions `content` field, wrapped in `<think>...</think>`. Split it out here so it can
 * render as its own collapsed block instead of leaking into the final answer as plain paragraphs.
 *
 * The other flavour — a separate `reasoning_content` field — arrives as `reasoning_delta` events
 * and lands on `AgentTurn.reasoning` instead (see `llm_client.rs`). A model uses one or the other,
 * never both, so `TurnBubble` prefers the streamed field and falls back to this. Reloaded turns
 * always come through here: reasoning is transient and never persisted.
 */
export function splitReasoning(content: string): ParsedAssistantContent {
  const reasoningParts: string[] = [];
  const answerParts: string[] = [];
  let cursor = 0;
  let streaming = false;

  while (cursor < content.length) {
    const start = content.indexOf(OPEN_TAG, cursor);
    if (start === -1) {
      answerParts.push(content.slice(cursor));
      break;
    }
    answerParts.push(content.slice(cursor, start));

    const afterOpen = start + OPEN_TAG.length;
    const end = content.indexOf(CLOSE_TAG, afterOpen);
    if (end === -1) {
      reasoningParts.push(content.slice(afterOpen));
      streaming = true;
      break;
    }
    reasoningParts.push(content.slice(afterOpen, end));
    cursor = end + CLOSE_TAG.length;
  }

  const reasoning = reasoningParts.join('\n\n').trim();
  return {
    reasoning: reasoning ? reasoning : null,
    answer: answerParts.join('').trim(),
    reasoningStreaming: streaming,
  };
}
